/**
 * The intro card (#1838): the screen dims, one card — NEW, a title, three
 * pictures with one line each, and a big Got it. Got it is remembered on the
 * person's account, so it never shows again for them.
 *
 * Place it on the screen where the feature lives:
 *
 *     <TeachIntro featureKey="grid.new" when={newGrid.on} />
 *
 * `when` is the screen's own "the feature is here and switched on for me".
 * The rules (role, once, after the spotlight, one at a time) live in
 * useTeachIntro + rules.ts.
 */
import { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { cn } from "@/lib/utils";
import { feature, type TeachFeature } from "./features";
import { TeachPicture } from "./pictures";
import { useTeachIntro } from "./teach-state";

export function TeachIntro({ featureKey, when }: { featureKey: string; when: boolean }) {
    const { show, done, later } = useTeachIntro(featureKey, when);
    const f = feature(featureKey);
    if (!show || !f?.intro) return null;
    return <TeachCard feature={f} onGotIt={done} onLater={later} />;
}

/** The card itself — also used by What's new → Show me again. */
export function TeachCard({
    feature: f,
    onGotIt,
    onLater,
    replay = false,
}: {
    feature: TeachFeature;
    onGotIt: () => void;
    onLater: () => void;
    replay?: boolean;
}) {
    const gotIt = useRef<HTMLButtonElement>(null);
    useEffect(() => {
        gotIt.current?.focus();
        const onKey = (e: KeyboardEvent) => {
            if (e.key === "Escape") onLater();
        };
        document.addEventListener("keydown", onKey);
        return () => document.removeEventListener("keydown", onKey);
    }, [onLater]);
    if (!f.intro) return null;
    const titleId = `teach-card-${f.key}`;
    return createPortal(
        <div
            className="fixed inset-0 z-[95] grid place-items-center overflow-y-auto bg-black/55 p-3 sm:p-6"
            data-testid={`teach-card-${f.key}`}
        >
            <div
                role="dialog"
                aria-modal="true"
                aria-labelledby={titleId}
                className="grid w-full max-w-[720px] gap-4 rounded-2xl bg-card p-4 text-card-foreground shadow-2xl sm:p-5"
            >
                <div className="flex flex-wrap items-center gap-2.5">
                    <span className="rounded bg-primary px-2 py-0.5 font-mono text-[11px] font-bold tracking-widest text-primary-foreground">
                        {replay ? "AGAIN" : "NEW"}
                    </span>
                    <h2 id={titleId} className="m-0 text-2xl font-extrabold leading-tight tracking-tight">
                        {f.intro.title}
                    </h2>
                </div>
                <ol className="m-0 grid list-none gap-3 p-0 sm:grid-cols-3">
                    {f.intro.steps.map((s, i) => (
                        <li key={i} className="grid content-start gap-2">
                            <TeachPicture pic={s.pic} className="aspect-[16/7] sm:aspect-[4/3]" />
                            <span className="font-mono text-[11px] font-bold text-primary">{i + 1}</span>
                            <p className="m-0 text-[15px] font-medium leading-snug">{s.line}</p>
                        </li>
                    ))}
                </ol>
                <div className="flex flex-wrap items-center justify-end gap-2">
                    <button
                        ref={gotIt}
                        type="button"
                        onClick={onGotIt}
                        className={cn(
                            "rounded-lg bg-primary px-7 py-3 text-lg font-bold text-primary-foreground shadow-sm",
                            "hover:bg-primary/90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary",
                        )}
                        data-testid="teach-got-it"
                    >
                        Got it
                    </button>
                </div>
            </div>
        </div>,
        document.body,
    );
}
